import React from "react";
import {IconButton, Menu, MenuItem, Tooltip, Typography} from "@mui/material";
import {DeleteForever, MoreVert, Update} from "@mui/icons-material";
import {PlatformTable} from "../PlatformTable";
import {withAppContext} from "../../util/withAppContext";
import withNavigate from "../../util/useNavigate";
import withBreakpoint from "../../util/withBreakpoint";
import {Blue3, Grey6, Grey7, Grey17} from "../../theme/styles";
import {classificationByCode} from "../../util/objectClassifications";
import {statusPill} from "../../util/statusPill";
import {hasPrivilege} from "../../util/SessionContext";
import {CHANGE_ORDER} from "../../util/changeOrderPrivileges";
import ChangeOrderMemberFormApplet from "../../eoec/applet/impl/Foundation/ChangeOrderMemberFormApplet";

/**
 * The records inside a change order.
 *
 * TWO SETS OF VALUES, AND THE DIFFERENCE IS THE POINT. `Record*` is a SNAPSHOT of what the record
 * was when it joined; `Current*` is what it is now. A member whose snapshot and current values
 * disagree has been revised since somebody put it in the package -- which is what a reviewer needs
 * to see before signing, and which the change order's content hash will already have used to mark
 * existing signatures stale.
 *
 * So the identifier column shows what was ADDED and flags the drift, rather than quietly showing
 * today's value and losing the fact that it moved.
 */
class ChangeOrderMemberTable extends PlatformTable {

    getCellRenderer(name) {
        if (name === "Member Id Cell") return this.renderIdCell;
        if (name === "Member Name Cell") return this.renderNameCell;
        if (name === "Member Status Cell") return this.renderStatusCell;
        if (name === "Action Cell") return this.renderActionCell;
        return super.getCellRenderer(name);
    }

    // EVERY VALUE BELOW EXCEPT FOUR IS UNPLACED, so this goes through readAttribute rather than
    // params.row -- see PlatformTable.readAttribute for what reading the row directly costs. The
    // revision numbers, the classification and the record's usid are all things the server returns
    // and no column shows; read off the row they are undefined and the cells degrade quietly.
    read = (params, field) => this.readAttribute(params, field);

    renderIdCell = (___, params, __, _) => {
        const objectId = this.read(params, "RecordObjectID");
        const addedRevision = this.read(params, "RecordDisplayRevision");
        const currentRevision = this.read(params, "CurrentDisplayRevision");

        const text = objectId
            ? (addedRevision ? `${objectId} REV ${addedRevision}` : objectId)
            : "—";

        const drifted = addedRevision !== null && currentRevision !== null
            && String(addedRevision) !== String(currentRevision);

        const body = (
            <div style={{display: "flex", alignItems: "center", gap: 4, height: "100%", minWidth: 0}}>
                <Typography style={{fontSize: 14, color: Grey17, overflow: "hidden",
                                    textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                    {text}
                </Typography>
                {drifted && <Update sx={{fontSize: 14, color: Grey6, flexShrink: 0}}/>}
            </div>
        );

        // The sentence only exists when there is something to say, and a Tooltip with no title still
        // changes the cursor -- so an ordinary row is not wrapped in one.
        return drifted
            ? <Tooltip placement="top"
                       title={`Added at REV ${addedRevision}. The record is now at REV ${currentRevision}, so signatures given before it moved are stale.`}>
                  {body}
              </Tooltip>
            : body;
    };

    renderNameCell = (___, params, __, _) => {
        // THE LIVE NAME, falling back to the snapshot. A rename is not a change to the record's
        // content, so showing today's name is right here -- unlike the revision, where which one was
        // reviewed is the whole question.
        const name = this.read(params, "CurrentName") || this.read(params, "RecordName");
        const revisionUSID = this.read(params, "RecordUSID");
        const route = classificationByCode(this.read(params, "ObjectClassification"))?.route;

        const cell = (content) => (
            <div style={{display: "flex", alignItems: "center", height: "100%", minWidth: 0}}>{content}</div>
        );

        if (!revisionUSID || !route) {
            return cell(<Typography style={{fontSize: 14, color: Grey7}}>{name || "—"}</Typography>);
        }

        return cell(
            <Typography onClick={() => this.props.navigate(`${route}/${revisionUSID}`)}
                        style={{fontSize: 14, fontWeight: 500, color: Blue3, cursor: "pointer",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                {name || "—"}
            </Typography>
        );
    };

    renderStatusCell = (___, params, __, _) => (
        <div style={{display: "flex", alignItems: "center", height: "100%"}}>
            {statusPill(this.read(params, "CurrentStatus"))}
        </div>
    );

    handleMenuClick(identifier) {
        if (identifier === "Add Change Order Member") {
            this.setState({renderTarget: "Add Change Order Member"});
            return;
        }
        super.handleMenuClick(identifier);
    }

    /**
     * The dialog for adding, and the one for removing.
     *
     * BOTH ARE THE SAME APPLET. A Delete action has no layout of its own, so the dialog borrows the
     * New one and forces every control disabled -- which is exactly what a confirmation should be:
     * the row you are about to remove, shown, and not editable.
     */
    renderExtension() {
        if (this.state.renderTarget === "Add Change Order Member") {
            return <ChangeOrderMemberFormApplet enterpriseComponent={this.applet.getEnterpriseComponent()}
                                                defaultAction="New"/>;
        }
        if (this.state.renderTarget === "Remove Change Order Member") {
            return <ChangeOrderMemberFormApplet enterpriseComponent={this.applet.getEnterpriseComponent()}
                                                defaultAction="Delete"/>;
        }
        return super.renderExtension();
    }

    renderActionCell = (instance, params, columnId, _) => {
        if (params.row.id > this.state.buffer.length - 1) return;

        const rowId = params.row.id;
        const open = this.state.actionMenuRowId === rowId && !!this.state.actionMenuAnchor;
        const closeMenu = {actionMenuAnchor: null, actionMenuRowId: null};

        // REMOVE IS THE ONLY THING IN THIS MENU, so without the privilege there is nothing to open
        // and the chevron itself goes. A menu that opens onto nothing reads as a broken control.
        if (!hasPrivilege(CHANGE_ORDER.REMOVE_RECORD)) return null;

        return (
            <div style={{width: "50px", backgroundColor: "#FFFFFF", marginLeft: "-10px"}}>
                <IconButton disableRipple sx={{"&:hover": {backgroundColor: "transparent"}}}
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                                event.stopPropagation();
                                this.applet.getEnterpriseComponent().selectRecord(rowId);
                                this.setState({actionMenuAnchor: event.currentTarget, actionMenuRowId: rowId});
                            }}>
                    <MoreVert sx={{fontSize: "1.35rem", position: "relative", top: "-1px"}}/>
                </IconButton>
                <Menu anchorEl={this.state.actionMenuAnchor}
                      anchorOrigin={{vertical: "center", horizontal: "right"}}
                      transformOrigin={{vertical: "top", horizontal: "left"}}
                      open={open}
                      onClose={() => this.setState(closeMenu)}
                      slotProps={{
                          paper: {elevation: 0, sx: {boxShadow: "none", borderRadius: 0, border: "1px solid #A8A9AF"}},
                          list: {disablePadding: true, sx: {minWidth: "220px", "& .MuiMenuItem-root": {minHeight: "33px", height: "33px"}}},
                          transition: {onExited: () => this.setState(closeMenu)},
                      }}>
                    {/* REMOVE FROM, NOT DELETE. The record itself is untouched -- only its membership
                        of this change order goes -- and "delete" beside a record's name reads like
                        the record is at risk. */}
                    <MenuItem key="remove" onClick={() => {
                        this.applet.getEnterpriseComponent().selectRecord(rowId);
                        this.setState({...closeMenu, renderTarget: "Remove Change Order Member"});
                    }}>
                        <DeleteForever style={{color: Grey6, marginRight: 8, fontSize: 20}}/>
                        <Typography style={{fontSize: "15px", fontWeight: "400", color: Grey17}}>
                            Remove from change order
                        </Typography>
                    </MenuItem>
                </Menu>
            </div>
        );
    };
}

export default withNavigate(withAppContext(withBreakpoint(ChangeOrderMemberTable)));
